import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Paper, Stack, Typography, Button, Alert, type ButtonProps } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useUploadGuard } from '../../hooks/useUploadGuard';
import { toUserFacingError, USER_FACING_ERROR_MESSAGES } from '../../utils/userFacingError';

export interface FileDropCardProps {
  /** Accept attribute passed to the hidden file input (e.g. ".pdf,.docx"). */
  accept?: string;
  /** Maximum size per file, in bytes. Files above this are rejected. */
  maxSizeBytes?: number;
  /** Allow multiple file selection. Defaults to false. */
  multiple?: boolean;
  /** Called with accepted File[] when the user drops or selects files. */
  onDrop: (files: File[]) => void;
  /** Heading rendered inside the drop zone. */
  title?: string;
  /** Supporting text rendered beneath the title. */
  description?: string;
  /** Icon rendered at the right of the title row. Defaults to UploadFileIcon. */
  icon?: React.ReactNode;
  /** Optional label for the pick-a-file button. Defaults to "Choose File". */
  buttonLabel?: string;
  /** Optional local action style; established callers retain outlined buttons. */
  buttonVariant?: ButtonProps['variant'];
  /** Whether to render the visible pick-a-file button. Defaults to true. */
  showPickerButton?: boolean;
  /** Optional accessible name for the hidden native file input. */
  inputAriaLabel?: string;
  /** Optional accessible name for the clickable drop zone. */
  dropzoneAriaLabel?: string;
  /** Optional test id for consumers that need to target this specific drop zone. */
  dataTestId?: string;
  /** Disable drop/click interactions. */
  disabled?: boolean;
}

/**
 * Shared drop zone used by the upload stages. Outlined Paper with a
 * dashed divider border; displays a title with a right-aligned icon, description, and,
 * optionally, a "Choose File" button. The card remains keyboard-operable
 * when the visible picker button is omitted. Rejects files above
 * `maxSizeBytes` and surfaces an inline Alert on rejection.
 */
export const FileDropCard: React.FC<FileDropCardProps> = ({
  accept,
  maxSizeBytes,
  multiple = false,
  onDrop,
  title = 'Drop a file here',
  description,
  icon,
  buttonLabel = 'Choose File',
  buttonVariant = 'outlined',
  showPickerButton = true,
  inputAriaLabel,
  dropzoneAriaLabel,
  dataTestId = 'file-drop-card',
  disabled = false,
}) => {
  const { disabledRef, openPicker: guardedOpenPicker } = useUploadGuard(disabled);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dragDepthRef = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (disabled) { dragDepthRef.current = 0; setIsDragging(false); }
  }, [disabled]);

  const validate = useCallback(
    (files: File[]): { accepted: File[]; rejected: File[] } => {
      const accepted: File[] = [];
      const rejected: File[] = [];
      for (const f of files) {
        if (maxSizeBytes && f.size > maxSizeBytes) {
          rejected.push(f);
        } else {
          accepted.push(f);
        }
      }
      return { accepted, rejected };
    },
    [maxSizeBytes]
  );

  const handleFiles = useCallback(
    (fileList: FileList | File[] | null) => {
      if (disabledRef.current || !fileList) return;
      const files = Array.from(fileList as ArrayLike<File>);
      const { accepted, rejected } = validate(files);
      if (rejected.length > 0) {
        // Client-side file-size validation is "fix_input" category — the
        // user can correct it by choosing a smaller file. Use the canonical
        // message so the JSX render does not sanitize it away.
        setError(USER_FACING_ERROR_MESSAGES.fix_input);
        return;
      }
      setError(null);
      if (accepted.length > 0) onDrop(accepted);
    },
    [onDrop, validate, disabledRef]
  );

  const onDropHandler = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragDepthRef.current = 0;
      setIsDragging(false);
      if (disabledRef.current) { e.stopPropagation(); return; }
      handleFiles(e.dataTransfer?.files ?? null);
    },
    [handleFiles, disabledRef]
  );

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabledRef.current) { e.stopPropagation(); return; }
    setIsDragging(true);
  }, [disabledRef]);

  const onDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (disabledRef.current) { e.stopPropagation(); return; }
    dragDepthRef.current += 1;
    setIsDragging(true);
  }, [disabledRef]);

  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragging(false);
  }, []);

  const openPicker = useCallback(() => {
    guardedOpenPicker(inputRef.current);
  }, [guardedOpenPicker]);

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    openPicker();
  }, [openPicker]);

  const defaultIcon = <UploadFileIcon />;
  const borderColor = error
    ? 'error.main'
    : isDragging
    ? 'primary.main'
    : 'divider';

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        variant="outlined"
        data-testid={dataTestId}
        data-dragging={isDragging ? 'true' : 'false'}
        onDrop={onDropHandler}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={openPicker}
        onKeyDown={onKeyDown}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled || undefined}
        aria-label={dropzoneAriaLabel}
        sx={{
          px: 2,
          py: 1.5,
          width: '100%',
          borderStyle: 'dashed',
          borderWidth: 1,
          borderColor,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.65 : 1,
          backgroundColor: isDragging ? 'primary.light' : 'transparent',
          transition: 'background-color 120ms ease, border-color 120ms ease',
        }}
        style={{ borderStyle: 'dashed' }}
      >
        <Stack spacing={1} alignItems="stretch" textAlign="left">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="h5" component="div" sx={{ minWidth: 0 }}>
              {title}
            </Typography>
            <Box data-testid="file-drop-icon" aria-hidden="true" sx={{ display: 'flex', flexShrink: 0, color: 'text.secondary', '& svg': { fontSize: 28 } }}>
              {icon ?? defaultIcon}
            </Box>
          </Box>
          {description && (
            <Typography
              variant="body2"
              color="text.secondary"
            >
              {description}
            </Typography>
          )}
          {showPickerButton && (
            <Button
              variant={buttonVariant}
              sx={{ alignSelf: 'flex-start' }}
              onClick={(e) => {
                e.stopPropagation();
                openPicker();
              }}
              disabled={disabled}
            >
              {buttonLabel}
            </Button>
          )}
        </Stack>
        <input
          ref={inputRef}
          type="file"
          disabled={disabled}
          accept={accept}
          multiple={multiple}
          hidden
          aria-label={inputAriaLabel}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            handleFiles(e.target.files);
            // Let a user select the same file again after an upload failure.
            e.currentTarget.value = '';
          }}
        />
      </Paper>
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {toUserFacingError(error)}
        </Alert>
      )}
    </Box>
  );
};

export default FileDropCard;
